import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { fetchCategories, fetchGigs } from "@/lib/api";
import GigCard from "@/components/GigCard";
import SEO from "@/components/SEO";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

export default function BrowsePage() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const [gigs, setGigs] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState(sp.get("q") || "");
  const [sort, setSort] = useState("recent");
  const [maxPrice, setMaxPrice] = useState("");
  const category = slug || sp.get("category") || "";
  const featuredOnly = sp.get("featured") === "1";

  useEffect(() => { fetchCategories().then(setCats).catch(() => {}); }, []);

  useEffect(() => {
    const params = { sort };
    if (q) params.q = q;
    if (category) params.category = category;
    if (maxPrice) params.max_price = Number(maxPrice);
    if (featuredOnly) params.featured = true;
    fetchGigs(params).then(setGigs).catch(() => setGigs([]));
  }, [q, category, sort, maxPrice, featuredOnly]);

  const catObj = cats.find((c) => c.slug === category);
  const seoTitle = featuredOnly
    ? "Editor's Picks — handpicked gigs"
    : catObj
      ? `${catObj.name} freelancers & gigs`
      : q
        ? `Search: ${q}`
        : "Browse all freelance gigs";
  const seoDesc = featuredOnly
    ? "A hand-curated list of top gigs on Noxi — vetted sellers, featured work."
    : catObj
      ? `Hire ${catObj.name.toLowerCase()} freelancers on Noxi. Verified sellers, fair prices, fast delivery.`
      : "Browse thousands of freelance gigs across design, programming, writing, video and more on Noxi.";

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
      <SEO title={seoTitle} description={seoDesc}/>
      <div className="mb-8 border-b border-black/10 pb-6">
        <div className="overline text-primary mb-2">◆ EXPLORE</div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
          {featuredOnly ? "Editor's Picks" : catObj ? catObj.name : q ? `Results for "${q}"` : "All gigs"}
        </h1>
        <div className="text-sm text-muted-foreground mono">{gigs.length} RESULTS</div>
      </div>

      <div className="grid md:grid-cols-[240px_1fr] gap-8">
        <aside className="space-y-6">
          <div>
            <div className="overline mb-3">◆ Search</div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5}/>
              <Input
                data-testid="browse-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search"
                className="pl-9 rounded-none border-foreground/20"
              />
            </div>
          </div>

          <div>
            <div className="overline mb-3">◆ Category</div>
            <div className="space-y-1">
              <button
                onClick={() => { sp.delete("category"); setSp(sp); window.history.replaceState({}, "", "/browse"); window.location.href = "/browse"; }}
                data-testid="filter-all"
                className={`block w-full text-left px-3 py-2 text-sm border ${!category ? "border-foreground bg-foreground text-background" : "border-transparent hover:border-foreground/30"}`}
              >
                All
              </button>
              {cats.map((c) => (
                <a
                  key={c.slug}
                  href={`/category/${c.slug}`}
                  data-testid={`filter-cat-${c.slug}`}
                  className={`block px-3 py-2 text-sm border ${category === c.slug ? "border-foreground bg-foreground text-background" : "border-transparent hover:border-foreground/30"}`}
                >
                  {c.name}
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="overline mb-3">◆ Max price</div>
            <Input
              data-testid="filter-max-price"
              type="number"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="e.g. 200"
              className="rounded-none border-foreground/20 mono"
            />
          </div>

          <div>
            <div className="overline mb-3">◆ Sort by</div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="filter-sort" className="rounded-none border-foreground/20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="recent">Most recent</SelectItem>
                <SelectItem value="rating">Top rated</SelectItem>
                <SelectItem value="price_asc">Price: low → high</SelectItem>
                <SelectItem value="price_desc">Price: high → low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </aside>

        <div>
          {gigs.length === 0 ? (
            <div className="border border-dashed border-foreground/20 p-16 text-center">
              <div className="overline mb-2">◆ EMPTY</div>
              <div className="text-lg font-medium">No gigs match your filters.</div>
              <p className="text-muted-foreground mt-2 text-sm">Try broadening your search or clearing filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {gigs.map((g, i) => (
                <div key={g.gig_id} className="fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                  <GigCard gig={g} testId={`browse-gig-${i}`} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
